/*
 * Kit add-on guard
 *
 * Add-on products (such as the extra house number digit) are only sold with
 * their kit. When a cart write deletes the last kit, its add-on lines are
 * removed inside the same request, so the cart redraws once with the kit and
 * its add-ons both gone.
 *
 * It works at the fetch level because two carts need it: the Section Store
 * drawer (app embed) and the theme's own cart page. Both write through
 * /cart/change or /cart/update and render whatever cart JSON comes back, so
 * handing them the cleaned cart updates either one.
 *
 * Rules and the server-rendered cart come from snippets/kit-addon-guard.liquid.
 */
(() => {
  const config = window.kitAddonGuard;
  if (!config || !Array.isArray(config.rules) || config.installed) return;
  config.installed = true;

  // Marks the guard's own cleanup request so it is not inspected again. The
  // Section Store drawer still sees it as an outside cart change and refreshes.
  const MARKER = 'kit_addon_guard=1';

  // add-on product id -> kit product ids that allow it in the cart
  const kitsForAddon = new Map();
  config.rules.forEach((rule) => {
    (rule.addons || []).forEach((addonId) => {
      const kits = kitsForAddon.get(Number(addonId)) || [];
      (rule.kits || []).forEach((kitId) => kits.push(Number(kitId)));
      kitsForAddon.set(Number(addonId), kits);
    });
  });
  if (!kitsForAddon.size) return;

  function productIdsOf(items) {
    return new Set((items || []).map((item) => Number(item.product_id)));
  }

  // Product ids in the cart as of the last cart JSON seen, seeded from the
  // server-rendered cart. A write only counts as deleting a kit if the kit was
  // here before it. Kaching adds the checkbox upsells in their own request
  // BEFORE the kit, so every purchase briefly has add-ons and no kit, and any
  // cart write landing in that gap must leave them alone.
  let productIdsInCart = new Set((config.cartProductIds || []).map(Number));

  // An add-on with none of its kits among `productIds`.
  function isOrphan(productId, productIds) {
    const kits = kitsForAddon.get(Number(productId));
    return Boolean(kits) && !kits.some((kitId) => productIds.has(kitId));
  }

  // Add-on lines with none of their kits in the cart. Given `before`, only the
  // ones whose kit was in the cart before this write, i.e. the write deleted it.
  function orphanKeys(cart, before) {
    const items = (cart && cart.items) || [];
    const inCart = productIdsOf(items);
    return items
      .filter((item) => isOrphan(item.product_id, inCart) && !(before && isOrphan(item.product_id, before)))
      .map((item) => item.key);
  }

  function routesRoot() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function removeLines(keys, params) {
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    console.info('[kit-addon-guard] removing add-ons whose kit is no longer in the cart', keys);
    return window.fetch(`${routesRoot()}cart/update.js?${MARKER}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, ...params }),
    });
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return (input && input.url) || '';
  }

  // 'write' for /cart/change and /cart/update, 'add' for /cart/add, 'read' for
  // the other calls that answer with the whole cart (/cart.js, /cart/clear).
  function cartCall(url) {
    if (!url || url.includes(MARKER)) return null;
    let path;
    try {
      path = new URL(url, window.location.href).pathname;
    } catch (error) {
      return null;
    }
    if (/\/cart\/(change|update)(\.js)?$/.test(path)) return 'write';
    if (/\/cart\/add(\.js)?$/.test(path)) return 'add';
    if (/\/cart(\.js|\/clear(\.js)?)$/.test(path)) return 'read';
    return null;
  }

  // Clones straight away, before the caller reads the body.
  function readJson(response) {
    // Shopify answers most /cart/*.js calls as text/javascript, not JSON.
    if (!response.ok || !/json|javascript/.test(response.headers.get('content-type') || '')) {
      return Promise.resolve(null);
    }
    return response
      .clone()
      .json()
      .catch(() => null);
  }

  function remember(data, call) {
    if (!data) return;
    if (call === 'add') {
      // /cart/add answers with the added line, or { items } when several were added.
      const added = Array.isArray(data.items) ? data.items : [data];
      productIdsInCart = new Set([...productIdsInCart, ...productIdsOf(added.filter((item) => item && item.product_id))]);
    } else if (Array.isArray(data.items)) {
      productIdsInCart = productIdsOf(data.items);
    }
  }

  // The sections the caller asked to have rendered, so the cleanup response can
  // carry the same HTML. Returns null when the request body cannot be read.
  function renderParams(body) {
    if (body == null) return {};
    let source;
    if (typeof body === 'string') {
      try {
        source = JSON.parse(body);
      } catch (error) {
        source = Object.fromEntries(new URLSearchParams(body));
      }
    } else if (body instanceof FormData || body instanceof URLSearchParams) {
      source = { sections: body.get('sections'), sections_url: body.get('sections_url') };
    } else {
      return null;
    }
    const params = {};
    if (source && source.sections) params.sections = source.sections;
    if (source && source.sections_url) params.sections_url = source.sections_url;
    return params;
  }

  async function settle(response, body) {
    const cart = await readJson(response);
    if (!cart || !Array.isArray(cart.items)) return response;
    const before = productIdsInCart;
    remember(cart, 'write');
    const keys = orphanKeys(cart, before);
    if (!keys.length) return response;

    const params = renderParams(body);
    if (!params) {
      // Can't repeat the caller's render request, so let it draw its own
      // response and tidy up straight after.
      setTimeout(() => cleanUp().catch(() => {}));
      return response;
    }
    const cleaned = await removeLines(keys, params);
    if (!cleaned.ok) return response;
    remember(await readJson(cleaned), 'write');
    return cleaned;
  }

  const previousFetch = window.fetch;
  window.fetch = function (input, init) {
    const request = previousFetch.apply(this, arguments);
    const call = cartCall(requestUrl(input));
    if (!call) return request;
    if (call !== 'write') {
      request.then((response) => readJson(response).then((data) => remember(data, call))).catch(() => {});
      return request;
    }
    // A Request object carries its own body, which can't be read here.
    const body = init && init.body != null ? init.body : input instanceof Request ? input : null;
    return request.then((response) => settle(response, body).catch(() => response));
  };

  // Outside a cart write (page load): the theme's cart page knows its own
  // sections, anywhere else only the header cart count needs redrawing. The
  // Section Store drawer refreshes itself when it sees the cleanup request.
  function sectionsToRender() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      try {
        return cartItems.getSectionsToRender();
      } catch (error) {
        // fall back to the header count
      }
    }
    if (!document.getElementById('cart-icon-bubble')) return [];
    return [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }

  function applySections(state, sections) {
    sections.forEach((section) => {
      const html = state.sections && state.sections[section.section];
      const host = document.getElementById(section.id);
      if (!html || !host) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (!source) return;
      (host.querySelector(section.selector) || host).innerHTML = source.innerHTML;
    });
    if (typeof state.item_count === 'number') {
      document
        .querySelectorAll('cart-items, #main-cart-footer')
        .forEach((element) => element.classList.toggle('is-empty', state.item_count === 0));
    }
  }

  async function cleanUp() {
    const response = await window.fetch(`${routesRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    const keys = orphanKeys(await response.json());
    if (!keys.length) return;

    const sections = sectionsToRender();
    const params = sections.length
      ? { sections: sections.map((section) => section.section), sections_url: window.location.pathname }
      : {};
    const cleaned = await removeLines(keys, params);
    if (!cleaned.ok) return;
    const state = await cleaned.json();
    remember(state, 'write');
    applySections(state, sections);
  }

  // The page was rendered with an add-on but no kit (the kit was removed some
  // other way, such as the no-JavaScript remove link), so tidy up on load. No
  // purchase can be half-added at this point, since a page load ends any
  // add-to-cart that was still running.
  if ([...productIdsInCart].some((id) => isOrphan(id, productIdsInCart))) {
    const run = () => cleanUp().catch(() => {});
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }
})();
